import { ModuleObject, RdkitInstance, RdkitModule, TypeMemory } from '../types';

/**
 * @private
 * @typedef {function} stackSave
 * @returns {number} The current top of the memory stack.
 */

/**
 * @private
 * @typedef {function} stackAlloc
 * @param {number} bytes The number of bytes to allocate on the stack.
 * @returns {number} Pointer to the allocated memory address.
 */

/**
 * @private
 * @typedef {function} stackRestore
 * @param {number} pointer The point of the memory stack to which to restore the stack.
 */

/**
 * @private
 * @typedef {object} Instance
 * @property {object} exports Exported C++ binding functions.
 * @property {stackSave} exports.stackSave The current top of the memory stack.
 * @property {stackAlloc} exports.stackAlloc Allocates the specified amount of bytes.
 * @property {stackRestore} exports.stackRestore Restores the memory stack to the provided point.
 * @property {object} memory Shared memory representations.
 * @property {Float32Array} memory.F32 Floating point 32 bit memory representation.
 * @property {Float64Array} memory.F64 Double floating point 64 bit memory representation.
 * @property {Int16Array} memory.S16 Signed 16 bit memory representation.
 * @property {Int32Array} memory.S32 Signed 32 bit memory representation.
 * @property {Int8Array} memory.S8 Signed 8 bit memory representation.
 * @property {Uint16Array} memory.U16 Unsigned 16 bit memory representation.
 * @property {Uint32Array} memory.U32 Unsigned 32 bit memory representation.
 * @property {Uint8Array} memory.U8 Unsigned 8 bit memory representation.
 */

/**
 * @type {RdkitInstance}
 * @private
 */
let privateGlobalInstance: RdkitInstance;

/**
 * @private
 * @returns {RdkitInstance}
 * @throws rdkit.js is not initialised.
 */
export const getInstance = (): RdkitInstance => {
  if (privateGlobalInstance) {
    return privateGlobalInstance;
  }

  throw new Error('rdkit is not initialised. initialise must be called first.');
};

function getTypedMemory(rdkitModule: RdkitModule): TypeMemory {
  return {
    S8: rdkitModule.HEAP8,
    S16: rdkitModule.HEAP16,
    S32: rdkitModule.HEAP32,
    U8: rdkitModule.HEAPU8,
    U16: rdkitModule.HEAPU16,
    U32: rdkitModule.HEAPU32,
    F32: rdkitModule.HEAPF32,
    F64: rdkitModule.HEAPF64,
  };
}

function internalInitialise(
  moduleObject: ModuleObject,
  rdkitModule: (moduleObject: ModuleObject) => Promise<RdkitModule>
): Promise<RdkitModule> {
  return new Promise(resolve => {
    rdkitModule(moduleObject).then((rdkitModule: RdkitModule) => {
      // prevent an infinite loop: https://github.com/emscripten-core/emscripten/issues/5820
      delete rdkitModule.then;
      resolve(rdkitModule);
    });
  });
}

/**
 * @returns {boolean} if the rdkit module is initialised
 */
export function isInitialised(): boolean {
  return !!privateGlobalInstance;
}

/**
 * Initialises the WebAssembly backend.
 * Needs to be called and finished before any other methods or classes can be used.
 * If no fileLocation is provided it assumes that the rdkit.wasm file is in the same directory as the rdkitjs.(cjs|esm).js file
 *
 * @async
 * @param {Function} rdkitModule module to use to initialise wasm (full or mini)
 * @param {string} fileLocation either the wasm path
 */
export async function initialise(
  rdkitModule: (moduleObject: ModuleObject) => Promise<RdkitModule>,
  fileLocation?: string
): Promise<void> {
  if (isInitialised()) {
    return;
  }

  const locateFile = fileLocation
    ? (path: string, prefix: string): string => {
        if (path.endsWith('.wasm')) {
          return fileLocation;
        }

        // keep default behaviour for non wasm files
        return prefix + path;
      }
    : undefined;

  const exports = await internalInitialise({ locateFile }, rdkitModule);
  const memory = getTypedMemory(exports);

  privateGlobalInstance = { memory, exports };
}
